<template>
  <div class="container-fluid pt-4 px-4 stock-analysis-page">
    <v-card flat class="pa-4 analysis-shell">
      <div class="d-flex flex-wrap justify-space-between align-start mb-4">
        <div>
          <h3 class="text-h5 font-weight-bold mb-1">Stock Analysis</h3>
          <p class="text-medium-emphasis mb-0">
            Review stock-wise performance, investment recovery, and near 52-week high or low opportunities.
          </p>
        </div>
      </div>

      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedReport"
            :items="reportOptions"
            item-title="label"
            item-value="value"
            label="Report Type"
            density="comfortable"
            variant="outlined"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.movementType"
            :items="movementOptions"
            item-title="label"
            item-value="value"
            label="Movement"
            density="comfortable"
            variant="outlined"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.stockIds"
            :items="stockOptions"
            item-title="label"
            item-value="code"
            label="Stocks"
            density="comfortable"
            variant="outlined"
            multiple
            chips
            closable-chips
            hide-details
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.mainPreset"
            :items="datePresetOptions"
            item-title="label"
            item-value="value"
            label="Date Preset"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:modelValue="applyDatePreset('main')"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model="filters.fromDate"
            type="date"
            label="From"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:modelValue="filters.mainPreset = 'CUSTOM'"
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model="filters.toDate"
            type="date"
            label="To"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:modelValue="filters.mainPreset = 'CUSTOM'"
          />
        </v-col>
        <v-col cols="12" md="1">
          <v-text-field
            v-model.number="filters.limit"
            type="number"
            label="Rows"
            density="comfortable"
            variant="outlined"
            min="1"
            max="500"
            hide-details
          />
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.comparePreset"
            :items="datePresetOptions"
            item-title="label"
            item-value="value"
            label="Compare Preset"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:modelValue="applyDatePreset('compare')"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="filters.compareFromDate"
            type="date"
            label="Compare From"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:modelValue="filters.comparePreset = 'CUSTOM'"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="filters.compareToDate"
            type="date"
            label="Compare To"
            density="comfortable"
            variant="outlined"
            hide-details
            @update:modelValue="filters.comparePreset = 'CUSTOM'"
          />
        </v-col>
        <v-col cols="12" md="3" class="d-flex align-center">
          <v-switch
            v-model="filters.enableCompare"
            color="primary"
            label="Enable Compare Chart"
            hide-details
          />
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12" md="2">
          <v-switch
            v-model="filters.onlyPortfolio"
            color="primary"
            label="Only Portfolio"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-switch
            v-model="filters.onlyFree"
            color="primary"
            label="Only Free"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-switch
            v-model="filters.useNearLow"
            color="primary"
            label="Near 52W Low"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model.number="filters.nearLowPercent"
            type="number"
            label="Near Low %"
            density="comfortable"
            variant="outlined"
            :disabled="!filters.useNearLow"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-switch
            v-model="filters.useNearHigh"
            color="primary"
            label="Near 52W High"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model.number="filters.nearHighPercent"
            type="number"
            label="Near High %"
            density="comfortable"
            variant="outlined"
            :disabled="!filters.useNearHigh"
            hide-details
          />
        </v-col>
      </v-row>

      <div class="d-flex gap-3 mt-4 flex-wrap">
        <v-btn color="primary" variant="tonal" rounded="lg" @click="fetchAnalysis">Apply Filters</v-btn>
        <v-btn variant="text" rounded="lg" @click="resetFilters">Reset</v-btn>
      </div>

      <div class="mt-3 report-caption">
        Viewing: {{ currentReportLabel }}
      </div>

      <template v-if="showActionSummary">
        <v-row class="mt-4">
          <v-col cols="12" md="3" v-for="card in actionSummaryCards" :key="card.title">
            <div class="action-card">
              <div class="action-card__title">{{ card.title }}</div>
              <div class="action-card__value">{{ card.value }}</div>
              <div class="action-card__hint">{{ card.hint }}</div>
            </div>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" md="7">
            <div class="insight-panel">
              <div class="insight-panel__title">Priority Actions</div>
              <div class="insight-panel__subtitle">Top stocks that need your attention first.</div>
              <div v-if="priorityActions.length" class="insight-list">
                <div v-for="item in priorityActions" :key="item.code" class="insight-item">
                  <div class="d-flex justify-space-between align-start gap-3 flex-wrap">
                    <div>
                      <strong>{{ item.code }}</strong> - {{ item.name }}
                      <div class="insight-meta">{{ item.action_signal }}</div>
                    </div>
                    <span class="signal-badge" :class="getSignalClass(item.action_signal)">{{ item.action_signal }}</span>
                  </div>
                  <div class="insight-meta mt-2">{{ getActionAdvice(item) }}</div>
                </div>
              </div>
              <div v-else class="insight-empty">No priority actions found for this filter.</div>
            </div>
          </v-col>
          <v-col cols="12" md="5">
            <div class="insight-panel">
              <div class="insight-panel__title">Recovery Capital</div>
              <div class="insight-panel__subtitle">Profitable stocks where partial selling can recover capital.</div>
              <div v-if="recoveryCandidates.length" class="insight-list">
                <div v-for="item in recoveryCandidates" :key="item.code" class="insight-item">
                  <div><strong>{{ item.code }}</strong> - {{ item.name }}</div>
                  <div class="insight-meta">
                    Recover Qty {{ formatNumber(item.qty_to_sell) }} | Recover {{ formatPercent(item.percentage_to_sell) }}
                  </div>
                </div>
              </div>
              <div v-else class="insight-empty">No recovery-capital candidates in this range.</div>
            </div>
          </v-col>
        </v-row>
      </template>

      <v-row v-if="showOverview" class="mt-4">
        <v-col cols="12" md="3" v-for="card in summaryCards" :key="card.title">
          <div class="summary-card">
            <div class="summary-card__label">{{ card.title }}</div>
            <div class="summary-card__value">{{ card.value }}</div>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="showCandidates" class="mt-4">
        <v-col cols="12" md="6">
          <div class="insight-panel">
            <div class="insight-panel__title">Average Candidates</div>
            <div class="insight-panel__subtitle">Loss-making stocks near lower levels.</div>
            <div v-if="averageCandidates.length" class="insight-list">
              <div v-for="item in averageCandidates" :key="item.code" class="insight-item">
                <div><strong>{{ item.code }}</strong> - {{ item.name }}</div>
                <div class="insight-meta">
                  Loss {{ $helper.formatPrice(item.profit_loss) }} | Near low {{ formatPercent(item.near_low_percent) }}
                </div>
              </div>
            </div>
            <div v-else class="insight-empty">No average candidates in this range.</div>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="insight-panel">
            <div class="insight-panel__title">Book Profit Candidates</div>
            <div class="insight-panel__subtitle">Profitable stocks near higher levels.</div>
            <div v-if="bookProfitCandidates.length" class="insight-list">
              <div v-for="item in bookProfitCandidates" :key="item.code" class="insight-item">
                <div><strong>{{ item.code }}</strong> - {{ item.name }}</div>
                <div class="insight-meta">
                  Profit {{ $helper.formatPrice(item.profit_loss) }} | Near high {{ formatPercent(item.near_high_percent) }}
                </div>
              </div>
            </div>
            <div v-else class="insight-empty">No profit-booking candidates in this range.</div>
          </div>
        </v-col>
      </v-row>

      <v-row class="mt-4" v-if="showTrendChange && filters.enableCompare">
        <v-col cols="12" md="6">
          <div class="insight-panel">
            <div class="insight-panel__title">Moved Into Loss</div>
            <div class="insight-panel__subtitle">These stocks were in profit before and are now in loss.</div>
            <div v-if="movedToLossCandidates.length" class="insight-list">
              <div v-for="item in movedToLossCandidates" :key="item.code" class="insight-item">
                <div><strong>{{ item.code }}</strong> - {{ item.name }}</div>
                <div class="insight-meta">{{ item.transition_status }}</div>
              </div>
            </div>
            <div v-else class="insight-empty">No stocks moved from profit to loss.</div>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="insight-panel">
            <div class="insight-panel__title">Moved Into Profit</div>
            <div class="insight-panel__subtitle">These stocks were in loss before and are now in profit.</div>
            <div v-if="movedToProfitCandidates.length" class="insight-list">
              <div v-for="item in movedToProfitCandidates" :key="item.code" class="insight-item">
                <div><strong>{{ item.code }}</strong> - {{ item.name }}</div>
                <div class="insight-meta">{{ item.transition_status }}</div>
              </div>
            </div>
            <div v-else class="insight-empty">No stocks moved from loss to profit.</div>
          </div>
        </v-col>
      </v-row>

      <div v-if="showCharts" class="chart-panel mt-4">
        <Bar
          v-if="chartReady"
          :data="chartData"
          :options="chartOptions"
        />
      </div>

      <div class="chart-panel mt-4" v-if="showCharts && compareChartReady">
        <Bar
          :data="compareChartData"
          :options="compareChartOptions"
        />
      </div>

      <v-data-table
        v-if="showTable"
        class="mt-4"
        :headers="headers"
        :items="rows"
        :items-per-page="10"
        fixed-header
      >
        <template v-slot:[`item.code`]="{ item }">
          <router-link :to="{ path: '/stock/view', query: { id: item.code } }" target="_blank">
            {{ item.code }}
          </router-link>
        </template>
        <template v-slot:[`item.investment`]="{ item }">{{ $helper.formatPrice(item.investment) }}</template>
        <template v-slot:[`item.current_value`]="{ item }">{{ $helper.formatPrice(item.current_value) }}</template>
        <template v-slot:[`item.market_price`]="{ item }">{{ $helper.formatPrice(item.market_price) }}</template>
        <template v-slot:[`item.initial_price`]="{ item }">{{ $helper.formatPrice(item.initial_price) }}</template>
        <template v-slot:[`item.final_price`]="{ item }">{{ $helper.formatPrice(item.final_price) }}</template>
        <template v-slot:[`item.price_change`]="{ item }">
          <span :class="getChangeClass(item.price_change)">{{ $helper.formatPrice(item.price_change) }}</span>
        </template>
        <template v-slot:[`item.price_change_percent`]="{ item }">
          <span :class="getChangeClass(item.price_change_percent)">{{ formatPercent(item.price_change_percent) }}</span>
        </template>
        <template v-slot:[`item.profit_loss`]="{ item }">{{ $helper.formatPrice(item.profit_loss) }}</template>
        <template v-slot:[`item.profit_loss_per`]="{ item }">{{ $helper.formatPercentage(item.profit_loss_per) }}</template>
        <template v-slot:[`item.total_sell`]="{ item }">{{ $helper.formatPrice(item.total_sell) }}</template>
        <template v-slot:[`item.percentage_to_sell`]="{ item }">{{ formatPercent(item.percentage_to_sell) }}</template>
        <template v-slot:[`item.qty_to_sell`]="{ item }">{{ formatNumber(item.qty_to_sell) }}</template>
        <template v-slot:[`item.near_low_percent`]="{ item }">{{ formatPercent(item.near_low_percent) }}</template>
        <template v-slot:[`item.near_high_percent`]="{ item }">{{ formatPercent(item.near_high_percent) }}</template>
        <template v-slot:[`item.transition_status`]="{ item }">
          <span class="signal-badge" :class="getTransitionClass(item.transition_status)">{{ item.transition_status }}</span>
        </template>
        <template v-slot:[`item.action_signal`]="{ item }">
          <span class="signal-badge" :class="getSignalClass(item.action_signal)">{{ item.action_signal }}</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { platformStore, useAlertStore } from '@/stores';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const STOCK_ANALYSIS_QUERY = gql`
  query StockAnalysis(
    $platformIds: [ID]
    $stockIds: [ID]
    $fromDate: String
    $toDate: String
    $movementType: String
    $nearLowPercent: Float
    $nearHighPercent: Float
    $onlyPortfolio: Boolean
    $onlyFree: Boolean
    $limit: Int
  ) {
    stockAnalysis(
      platform_ids: $platformIds
      stock_ids: $stockIds
      from_date: $fromDate
      to_date: $toDate
      movement_type: $movementType
      near_low_percent: $nearLowPercent
      near_high_percent: $nearHighPercent
      only_portfolio: $onlyPortfolio
      only_free: $onlyFree
      limit: $limit
    ) {
      stockData {
        stock_id
        name
        code
        qty
        investment
        current_value
        market_price
        profit_loss
        profit_loss_per
        total_sell
        percentage_to_sell
        qty_to_sell
        initial_price
        final_price
        price_change
        price_change_percent
        near_low_percent
        near_high_percent
      }
      chartData {
        code
        price_change_percent
      }
      gridTotals {
        qty
        investment
        current_value
        profit_loss
        profit_loss_per
      }
    }
  }
`;

export default {
  name: 'StockAnalysis',
  components: { Bar },
  data() {
    const today = new Date();
    const previous = new Date();
    previous.setDate(today.getDate() - 7);
    const formatInputDate = (date) => {
      const offset = date.getTimezoneOffset();
      return new Date(date.getTime() - offset * 60000).toISOString().split('T')[0];
    };

    return {
      filters: {
        stockIds: [],
        movementType: 'ALL',
        mainPreset: 'LAST_7_DAYS',
        fromDate: formatInputDate(previous),
        toDate: formatInputDate(today),
        comparePreset: 'LAST_MONTH',
        compareFromDate: '',
        compareToDate: '',
        enableCompare: true,
        onlyPortfolio: true,
        onlyFree: false,
        useNearLow: false,
        nearLowPercent: 5,
        useNearHigh: false,
        nearHighPercent: 5,
        limit: 100
      },
      rows: [],
      totals: {},
      chartRows: [],
      compareRows: [],
      compareStockData: [],
      selectedReport: 'ALL_REPORTS',
      stockOptions: [],
      reportOptions: [
        { label: 'Action Summary', value: 'ACTION_SUMMARY' },
        { label: 'Overview', value: 'OVERVIEW' },
        { label: 'Candidates', value: 'CANDIDATES' },
        { label: 'Trend Change', value: 'TREND_CHANGE' },
        { label: 'Charts', value: 'CHARTS' },
        { label: 'Table', value: 'TABLE' },
        { label: 'All Reports', value: 'ALL_REPORTS' }
      ],
      movementOptions: [
        { label: 'All', value: 'ALL' },
        { label: 'Top Gain', value: 'TOP_GAIN' },
        { label: 'Top Loss', value: 'TOP_LOSS' }
      ],
      datePresetOptions: [
        { label: 'Custom Range', value: 'CUSTOM' },
        { label: 'Today', value: 'TODAY' },
        { label: 'Yesterday', value: 'YESTERDAY' },
        { label: 'Last 7 Days', value: 'LAST_7_DAYS' },
        { label: 'Last 15 Days', value: 'LAST_15_DAYS' },
        { label: 'Last 30 Days', value: 'LAST_30_DAYS' },
        { label: 'Last 90 Days', value: 'LAST_90_DAYS' },
        { label: 'This Week', value: 'THIS_WEEK' },
        { label: 'Last Week', value: 'LAST_WEEK' },
        { label: 'This Month', value: 'THIS_MONTH' },
        { label: 'Last Month', value: 'LAST_MONTH' },
        { label: 'Last 3 Months', value: 'LAST_3_MONTHS' },
        { label: 'Last 6 Months', value: 'LAST_6_MONTHS' },
        { label: `This Year (${today.getFullYear()})`, value: 'THIS_YEAR' },
        { label: `Last Year (${today.getFullYear() - 1})`, value: 'LAST_YEAR' }
      ],
      headers: [
        { title: 'Code', key: 'code' },
        { title: 'Name', key: 'name' },
        { title: 'Qty', key: 'qty' },
        { title: 'Investment', key: 'investment' },
        { title: 'Current Value', key: 'current_value' },
        { title: 'Current Price', key: 'market_price' },
        { title: 'Start Price', key: 'initial_price' },
        { title: 'End Price', key: 'final_price' },
        { title: 'Change', key: 'price_change' },
        { title: 'Change %', key: 'price_change_percent' },
        { title: 'P/L', key: 'profit_loss' },
        { title: 'P/L %', key: 'profit_loss_per' },
        { title: 'Total Sell', key: 'total_sell' },
        { title: 'Recover %', key: 'percentage_to_sell' },
        { title: 'Recover Qty', key: 'qty_to_sell' },
        { title: 'Near Low %', key: 'near_low_percent' },
        { title: 'Near High %', key: 'near_high_percent' },
        { title: 'Trend Status', key: 'transition_status' },
        { title: 'Action', key: 'action_signal' }
      ],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: { display: true, text: 'Top Price Change %' }
        }
      },
      compareChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'top' },
          title: { display: true, text: 'Current Range vs Compare Range' }
        }
      }
    };
  },
  computed: {
    currentReportLabel() {
      return this.reportOptions.find((item) => item.value === this.selectedReport)?.label || 'All Reports';
    },
    showActionSummary() {
      return ['ACTION_SUMMARY', 'ALL_REPORTS'].includes(this.selectedReport);
    },
    showOverview() {
      return ['OVERVIEW', 'ALL_REPORTS'].includes(this.selectedReport);
    },
    showCandidates() {
      return ['CANDIDATES', 'ALL_REPORTS'].includes(this.selectedReport);
    },
    showTrendChange() {
      return ['TREND_CHANGE', 'ALL_REPORTS'].includes(this.selectedReport);
    },
    showCharts() {
      return ['CHARTS', 'ALL_REPORTS'].includes(this.selectedReport);
    },
    showTable() {
      return ['TABLE', 'ALL_REPORTS'].includes(this.selectedReport);
    },
    summaryCards() {
      return [
        { title: 'Stocks', value: String(this.rows.length) },
        { title: 'Investment', value: this.$helper.formatPrice(this.totals.investment || 0) },
        { title: 'Current Value', value: this.$helper.formatPrice(this.totals.current_value || 0) },
        { title: 'Profit / Loss', value: this.$helper.formatPrice(this.totals.profit_loss || 0) }
      ];
    },
    actionSummaryCards() {
      const averageCount = this.rows.filter((item) => item.action_signal.includes('Average')).length;
      const bookProfitCount = this.rows.filter((item) => item.action_signal.includes('Book Profit')).length;
      const recoverCount = this.rows.filter((item) => item.action_signal.includes('Recover')).length;
      const holdCount = this.rows.filter((item) => item.action_signal.includes('Hold') || item.action_signal === 'Neutral').length;

      return [
        { title: 'Average Watch', value: String(averageCount), hint: 'Potential averaging review' },
        { title: 'Book Profit', value: String(bookProfitCount), hint: 'Profits available to lock' },
        { title: 'Recover Capital', value: String(recoverCount), hint: 'Partial exit can de-risk' },
        { title: 'Hold / Neutral', value: String(holdCount), hint: 'No strong action yet' }
      ];
    },
    priorityActions() {
      return [...this.rows]
        .sort((a, b) => {
          const priorityDiff = this.getActionPriority(a) - this.getActionPriority(b);
          if (priorityDiff !== 0) return priorityDiff;
          return Math.abs(Number(b.profit_loss) || 0) - Math.abs(Number(a.profit_loss) || 0);
        })
        .slice(0, 8);
    },
    recoveryCandidates() {
      return [...this.rows]
        .filter((item) => Number(item.qty_to_sell) > 0 && Number(item.profit_loss) > 0)
        .sort((a, b) => Number(a.percentage_to_sell) - Number(b.percentage_to_sell))
        .slice(0, 6);
    },
    averageCandidates() {
      return [...this.rows]
        .filter((item) => Number(item.profit_loss) < 0 && Number(item.near_low_percent) <= 8)
        .sort((a, b) => Number(a.near_low_percent) - Number(b.near_low_percent))
        .slice(0, 5);
    },
    bookProfitCandidates() {
      return [...this.rows]
        .filter((item) => Number(item.profit_loss) > 0 && Number(item.near_high_percent) <= 8)
        .sort((a, b) => Number(a.near_high_percent) - Number(b.near_high_percent))
        .slice(0, 5);
    },
    movedToLossCandidates() {
      return this.rows.filter((item) => item.transition_status === 'Profit -> Loss').slice(0, 5);
    },
    movedToProfitCandidates() {
      return this.rows.filter((item) => item.transition_status === 'Loss -> Profit').slice(0, 5);
    },
    chartReady() {
      return this.chartRows.length > 0;
    },
    chartData() {
      return {
        labels: this.chartRows.map((item) => item.code),
        datasets: [
          {
            label: 'Price Change %',
            data: this.chartRows.map((item) => item.price_change_percent),
            backgroundColor: this.chartRows.map((item) => item.price_change_percent >= 0 ? '#16A34A' : '#DC2626'),
            borderRadius: 8
          }
        ]
      };
    },
    compareChartReady() {
      return this.filters.enableCompare && this.chartRows.length > 0 && this.compareRows.length > 0;
    },
    compareChartData() {
      const currentMap = new Map(this.chartRows.map((item) => [item.code, item.price_change_percent]));
      const compareMap = new Map(this.compareRows.map((item) => [item.code, item.price_change_percent]));
      const labels = Array.from(new Set([...currentMap.keys(), ...compareMap.keys()])).slice(0, 10);

      return {
        labels,
        datasets: [
          {
            label: `${this.filters.fromDate} to ${this.filters.toDate}`,
            data: labels.map((label) => currentMap.get(label) ?? 0),
            backgroundColor: '#2563EB',
            borderRadius: 8
          },
          {
            label: `${this.filters.compareFromDate} to ${this.filters.compareToDate}`,
            data: labels.map((label) => compareMap.get(label) ?? 0),
            backgroundColor: '#D97706',
            borderRadius: 8
          }
        ]
      };
    }
  },
  methods: {
    formatInputDate(date) {
      const offset = date.getTimezoneOffset();
      return new Date(date.getTime() - offset * 60000).toISOString().split('T')[0];
    },
    formatPercent(value) {
      return `${(Number(value) || 0).toFixed(2)}%`;
    },
    formatNumber(value) {
      return Number(value || 0).toFixed(2);
    },
    getActionPriority(item) {
      const signal = item.action_signal || '';

      if (signal.includes('Book Profit / Recover Capital')) return 1;
      if (signal.includes('Average')) return 2;
      if (signal === 'Book Profit') return 3;
      if (signal.includes('Recover')) return 4;
      if (signal.includes('Hold')) return 5;
      return 6;
    },
    getActionSignal(item) {
      const profitLoss = Number(item.profit_loss) || 0;
      const nearLow = Number(item.near_low_percent);
      const nearHigh = Number(item.near_high_percent);
      const recoverQty = Number(item.qty_to_sell) || 0;

      if (profitLoss < 0 && Number.isFinite(nearLow) && nearLow <= 5) {
        return 'Average Watch';
      }

      if (profitLoss > 0 && Number.isFinite(nearHigh) && nearHigh <= 5) {
        return recoverQty > 0 ? 'Book Profit / Recover Capital' : 'Book Profit';
      }

      if (profitLoss > 0 && recoverQty > 0) {
        return 'Recover Capital';
      }

      if (profitLoss < 0) {
        return 'Hold / Review';
      }

      return 'Neutral';
    },
    getActionAdvice(item) {
      const signal = item.action_signal || '';

      if (signal.includes('Book Profit / Recover Capital')) {
        return `Consider selling about ${this.formatNumber(item.qty_to_sell)} qty to recover capital and keep the remaining position running.`;
      }

      if (signal.includes('Average')) {
        return 'Loss is near lower zone. Review business quality and average only in small planned quantity.';
      }

      if (signal === 'Book Profit') {
        return 'Stock is profitable and close to a higher zone. Partial profit booking may protect gains.';
      }

      if (signal.includes('Recover')) {
        return `You can recover capital by selling about ${this.formatNumber(item.qty_to_sell)} qty.`;
      }

      if (signal.includes('Hold')) {
        return 'No clean sell signal yet. Monitor trend strength and avoid rushed averaging.';
      }

      return 'No strong action signal at the moment.';
    },
    getTransitionStatus(currentItem, compareItem) {
      if (!this.filters.enableCompare || !compareItem) {
        return 'No Compare';
      }

      const currentRangeReturn = Number(currentItem.price_change_percent) || 0;
      const compareRangeReturn = Number(compareItem.price_change_percent) || 0;

      if (compareRangeReturn > 0 && currentRangeReturn < 0) {
        return 'Profit -> Loss';
      }

      if (compareRangeReturn < 0 && currentRangeReturn > 0) {
        return 'Loss -> Profit';
      }

      if (compareRangeReturn < 0 && currentRangeReturn < 0) {
        return 'Still in Loss';
      }

      if (compareRangeReturn > 0 && currentRangeReturn > 0) {
        return 'Still in Profit';
      }

      return 'Flat / Mixed';
    },
    getSignalClass(signal) {
      if (signal.includes('Average')) return 'signal-badge--average';
      if (signal.includes('Book Profit')) return 'signal-badge--profit';
      if (signal.includes('Recover')) return 'signal-badge--recover';
      if (signal.includes('Hold')) return 'signal-badge--hold';
      return 'signal-badge--neutral';
    },
    getTransitionClass(signal) {
      if (signal.includes('Profit -> Loss')) return 'signal-badge--hold';
      if (signal.includes('Loss -> Profit')) return 'signal-badge--profit';
      if (signal.includes('Still in Loss')) return 'signal-badge--average';
      if (signal.includes('Still in Profit')) return 'signal-badge--recover';
      return 'signal-badge--neutral';
    },
    getStartOfWeek(date) {
      const start = new Date(date);
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diff);
      start.setHours(0, 0, 0, 0);
      return start;
    },
    getDatePresetRange(preset) {
      const today = new Date();
      let start;
      let end;

      switch (preset) {
        case 'TODAY':
          start = new Date(today);
          end = new Date(today);
          break;
        case 'YESTERDAY':
          start = new Date(today);
          start.setDate(today.getDate() - 1);
          end = new Date(start);
          break;
        case 'LAST_7_DAYS':
          start = new Date(today);
          start.setDate(today.getDate() - 7);
          end = new Date(today);
          break;
        case 'LAST_15_DAYS':
          start = new Date(today);
          start.setDate(today.getDate() - 15);
          end = new Date(today);
          break;
        case 'LAST_30_DAYS':
          start = new Date(today);
          start.setDate(today.getDate() - 30);
          end = new Date(today);
          break;
        case 'LAST_90_DAYS':
          start = new Date(today);
          start.setDate(today.getDate() - 90);
          end = new Date(today);
          break;
        case 'THIS_WEEK':
          start = this.getStartOfWeek(today);
          end = new Date(today);
          break;
        case 'LAST_WEEK': {
          const currentWeekStart = this.getStartOfWeek(today);
          end = new Date(currentWeekStart);
          end.setDate(end.getDate() - 1);
          start = this.getStartOfWeek(end);
          break;
        }
        case 'THIS_MONTH':
          start = new Date(today.getFullYear(), today.getMonth(), 1);
          end = new Date(today);
          break;
        case 'LAST_MONTH':
          start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          end = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        case 'LAST_3_MONTHS':
          start = new Date(today);
          start.setMonth(today.getMonth() - 3);
          end = new Date(today);
          break;
        case 'LAST_6_MONTHS':
          start = new Date(today);
          start.setMonth(today.getMonth() - 6);
          end = new Date(today);
          break;
        case 'THIS_YEAR':
          start = new Date(today.getFullYear(), 0, 1);
          end = new Date(today);
          break;
        case 'LAST_YEAR':
          start = new Date(today.getFullYear() - 1, 0, 1);
          end = new Date(today.getFullYear() - 1, 11, 31);
          break;
        default:
          return null;
      }

      return {
        fromDate: this.formatInputDate(start),
        toDate: this.formatInputDate(end)
      };
    },
    applyDatePreset(type) {
      const preset = type === 'main' ? this.filters.mainPreset : this.filters.comparePreset;
      const range = this.getDatePresetRange(preset);
      if (!range) return;

      if (type === 'main') {
        this.filters.fromDate = range.fromDate;
        this.filters.toDate = range.toDate;
      } else {
        this.filters.compareFromDate = range.fromDate;
        this.filters.compareToDate = range.toDate;
      }
    },
    getChangeClass(value) {
      return Number(value) >= 0 ? 'text-success font-weight-bold' : 'text-error font-weight-bold';
    },
    async fetchStockOptions() {
      const response = await this.$apollo.query({
        query: gql`
          query {
            stocks {
              stock_id
              name
              code
            }
          }
        `
      });

      const unique = new Map();
      (response.data?.stocks || []).forEach((stock) => {
        if (!unique.has(stock.stock_id)) {
          unique.set(stock.stock_id, {
            code: stock.stock_id,
            label: `${stock.code} - ${stock.name}`
          });
        }
      });
      this.stockOptions = Array.from(unique.values());
    },
    async fetchAnalysis() {
      try {
        const baseVariables = {
          platformIds: JSON.parse(this.platformOptions || '[]'),
          stockIds: this.filters.stockIds,
          movementType: this.filters.movementType,
          nearLowPercent: this.filters.useNearLow ? Number(this.filters.nearLowPercent) : null,
          nearHighPercent: this.filters.useNearHigh ? Number(this.filters.nearHighPercent) : null,
          onlyPortfolio: this.filters.onlyPortfolio,
          onlyFree: this.filters.onlyFree,
          limit: Number(this.filters.limit) || 100
        };

        const requests = [
          this.$apollo.query({
            query: STOCK_ANALYSIS_QUERY,
            variables: {
              ...baseVariables,
              fromDate: this.filters.fromDate || null,
              toDate: this.filters.toDate || null
            },
            fetchPolicy: 'network-only'
          })
        ];

        if (this.filters.enableCompare && this.filters.compareFromDate && this.filters.compareToDate) {
          requests.push(
            this.$apollo.query({
              query: STOCK_ANALYSIS_QUERY,
              variables: {
                ...baseVariables,
                fromDate: this.filters.compareFromDate || null,
                toDate: this.filters.compareToDate || null
              },
              fetchPolicy: 'network-only'
            })
          );
        }

        const [currentResponse, compareResponse] = await Promise.all(requests);
        const data = currentResponse?.data;
        const compareStockData = compareResponse?.data?.stockAnalysis?.stockData || [];
        const compareStockMap = new Map(compareStockData.map((item) => [item.code, item]));

        this.rows = (data?.stockAnalysis?.stockData || []).map((item) => ({
          ...item,
          transition_status: this.getTransitionStatus(item, compareStockMap.get(item.code)),
          action_signal: this.getActionSignal(item)
        }));
        this.totals = data?.stockAnalysis?.gridTotals || {};
        this.chartRows = data?.stockAnalysis?.chartData || [];
        this.compareRows = compareResponse?.data?.stockAnalysis?.chartData || [];
        this.compareStockData = compareStockData;
      } catch (error) {
        this.alertStore$.error(JSON.stringify(error.message || error));
      }
    },
    resetFilters() {
      const today = new Date();
      const previous = new Date();
      previous.setDate(today.getDate() - 7);
      this.selectedReport = 'ALL_REPORTS';
      this.filters = {
        stockIds: [],
        movementType: 'ALL',
        mainPreset: 'LAST_7_DAYS',
        fromDate: this.formatInputDate(previous),
        toDate: this.formatInputDate(today),
        comparePreset: 'LAST_MONTH',
        compareFromDate: '',
        compareToDate: '',
        enableCompare: true,
        onlyPortfolio: true,
        onlyFree: false,
        useNearLow: false,
        nearLowPercent: 5,
        useNearHigh: false,
        nearHighPercent: 5,
        limit: 100
      };
      this.applyDatePreset('main');
      this.applyDatePreset('compare');
      this.fetchAnalysis();
    }
  },
  async mounted() {
    this.applyDatePreset('main');
    this.applyDatePreset('compare');
    await this.fetchStockOptions();
    await this.fetchAnalysis();
  },
  setup() {
    const store = platformStore();
    const { platform } = storeToRefs(store);
    return {
      alertStore$: useAlertStore(),
      platformOptions: platform
    };
  }
};
</script>

<style scoped>
.stock-analysis-page {
  background: var(--light);
  min-height: 100%;
}

.analysis-shell {
  border-radius: 12px;
  background: #fff;
  border: 1px solid var(--border);
}

.summary-card {
  padding: 18px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid var(--border);
  color: var(--dark);
  min-height: 110px;
}

.action-card {
  padding: 18px;
  border-radius: 12px;
  min-height: 120px;
  background: #fff;
  border: 1px solid var(--border);
}

.action-card__title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.action-card__value {
  margin-top: 10px;
  font-size: 30px;
  line-height: 1;
  font-weight: 700;
  color: var(--dark);
}

.action-card__hint {
  margin-top: 8px;
  font-size: 13px;
  color: var(--text-muted);
}

.summary-card__label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.summary-card__value {
  font-size: 26px;
  font-weight: 700;
  color: var(--dark);
}

.chart-panel {
  height: 360px;
  padding: 20px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid var(--border);
}

.insight-panel {
  height: 100%;
  padding: 20px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid var(--border);
}

.insight-panel__title {
  font-size: 18px;
  font-weight: 700;
  color: var(--dark);
}

.insight-panel__subtitle {
  margin-top: 4px;
  color: var(--text-muted);
  font-size: 14px;
}

.insight-list {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.insight-item {
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--light);
  border: 1px solid var(--border);
}

.insight-meta {
  margin-top: 4px;
  color: var(--text-muted);
  font-size: 13px;
}

.insight-empty {
  margin-top: 16px;
  color: var(--text-muted);
}

.signal-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.signal-badge--average {
  background: #dbeafe;
  color: #1d4ed8;
}

.signal-badge--profit {
  background: #dcfce7;
  color: #166534;
}

.signal-badge--recover {
  background: #fef3c7;
  color: #b45309;
}

.signal-badge--hold {
  background: #fee2e2;
  color: #b91c1c;
}

.signal-badge--neutral {
  background: #e2e8f0;
  color: #334155;
}

.report-caption {
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
}
</style>
